
import React, { Fragment, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ModalVideo from "react-modal-video";
import { Helmet } from "react-helmet-async";

import Header from "../../components/header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import CtaSection from "../../components/CtaSection/CtaSection";
import ProjectSection from "../../components/ProjectSection/ProjectSection";
import BlogSection from "../../components/BlogSection/BlogSection";

import Stats from "../../components/Stats/Stats";

import icon from "../../images/icons/icon_check_3.svg";
import industries from "../../api/industries";
import { useSeo } from "../../hooks/useSeo";
import Faq from "../../components/industries/faq";
import IndustryComplete from "../../components/Details/Details";
import IndustriesProjectSection from "../../components/ProjectSection/Industries_Project_Section";
import IndustriesBlogSection from "../../components/BlogSection/IndustriesBlogSection";
import WhyChooseUs from "./WhyChooseUs";

// Meta data mapping based on slug
const metaDataMap = {
  healthcare: {
    title: "Healthcare IT Solutions in Alwar, India | RashTech Info",
    description: "RashTech Info offers healthcare IT solutions, medical apps & hospital software development in Alwar and across India."
  },
  education: {
    title: "E-Learning & Education Software in Alwar, India | RashTech Info",
    description: "Smart e-learning platforms, LMS & education apps by RashTech Info in Alwar, serving schools & institutes across India."
  },
  finance: {
    title: "FinTech & Finance Software in Alwar, India | RashTech Info",
    description: "RashTech Info develops secure FinTech apps, banking solutions & finance software for clients in Alwar and all over India."
  },
  "legal-consultancy": {
    title: "Legal & Consultancy Software in Alwar, India | RashTech Info",
    description: "Automate contracts & case management with legal software solutions by RashTech Info, serving Alwar & India-wide clients."
  },
  logistics: {
    title: "Logistics & Supply Chain Software in Alwar, India | RashTech Info",
    description: "Optimize logistics, fleet & supply chain operations with RashTech Info's software solutions in Alwar and across India."
  },
  insurance: {
    title: "Insurance Software Development in Alwar, India | RashTech Info",
    description: "RashTech Info provides insurance apps, claim management & policy software solutions in Alwar and throughout India."
  },
  manufacturing: {
    title: "Manufacturing Software Solutions in Alwar, India | RashTech Info",
    description: "Boost efficiency with ERP & automation software by RashTech Info for manufacturing industries in Alwar & pan India."
  },
  travel: {
    title: "Travel & Tourism Software in Alwar, India | RashTech Info",
    description: "RashTech Info develops travel booking, tourism & hotel management apps in Alwar, serving businesses across India."
  },
  retail: {
    title: "Retail & eCommerce Software in Alwar, India | RashTech Info",
    description: "Grow your retail & eCommerce business with POS & custom software by RashTech Info in Alwar and all over India."
  },
  construction: {
    title: "Construction Software Solutions in Alwar, India | RashTech Info",
    description: "RashTech Info delivers project management & real estate software for construction companies in Alwar & across India."
  },
  sports: {
    title: "Sports & Fitness Software in Alwar, India | RashTech Info",
    description: "RashTech Info builds sports apps, fitness trackers & digital platforms for clients in Alwar and across India."
  },
  marketplace: {
    title: "Online Marketplace Development in Alwar, India | RashTech Info",
    description: "Create scalable marketplace platforms & multi-vendor apps with RashTech Info, serving Alwar & pan India businesses."
  },
  "beauty-lifestyle": {
    title: "Beauty & Lifestyle Apps in Alwar, India | RashTech Info",
    description: "RashTech Info develops beauty, wellness & lifestyle apps for salons & brands in Alwar and across India."
  },
  "on-demand": {
    title: "On-Demand App Development in Alwar, India | RashTech Info",
    description: "RashTech Info builds custom on-demand apps for delivery & services in Alwar, serving startups & enterprises across India."
  }
};

const staticMeta = {
  title: "Industries | RashTech Info",
  description: "Detailed view of our industry offerings at RashTech Info, including outcomes, capabilities, and approach.",
  url: "https://rashtechinfo.com/industries",
  image: "https://rashtechinfo.com/og-image.png",
  siteName: "RashTech Info",
  keywords: "industries, offering, outcomes, IT services, RashTech Info",
  themeColor: "#0A0D1C",
};

const IndustriesSinglePage = () => {
  const { slug } = useParams();

  const industry = industries.find((item) => item.slug === slug);
  
  const [isOpen, setOpen] = useState(false);

  const { seo } = useSeo("/getSeo");

  const computed = useMemo(() => {
    // Get dynamic meta data based on slug
    const dynamicMeta = metaDataMap[slug] || {};
    
    const dynamic = {
      title: seo?.title,
      description: seo?.metaDescription,
      keywords: seo?.metaKeywords,
      canonicalUrl: seo?.canonicalUrl,
    };

    // Priority: dynamicMeta (slug-based) > seo API > staticMeta
    const pageTitle = dynamicMeta.title || 
                     (industry ? `${industry.title} | Industries | RashTech Info` : null) ||
                     dynamic.title || 
                     staticMeta.title;

    const description = dynamicMeta.description || 
                       industry?.description || 
                       dynamic.description || 
                       staticMeta.description;

    return {
      title: pageTitle,
      description,
      keywords: dynamic.keywords || staticMeta.keywords,
      url: dynamic.canonicalUrl ||
          (slug ? `https://rashtechinfo.com/industries/${slug}` : staticMeta.url),
      image: staticMeta.image,
      siteName: staticMeta.siteName,
      themeColor: staticMeta.themeColor,
    };
  }, [seo, industry, slug]);

  // Simple not-found guard
  if (!industry) {
    return (
      <Fragment>
        <Helmet>
          <title>{staticMeta.title}</title>
          <meta name="description" content={staticMeta.description} />
        </Helmet>
        <Header />
        <main className="page_content service-single-page">
          <PageTitle
            pageTitle="Industry not found"
            pagesub="Details"
            pageTop="Industries"
          />
          <section className="section_space">
            <div className="container">
              <p>Sorry, the requested industry could not be found.</p>
            </div>
          </section>
        </main>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>{computed.title}</title>
        <meta name="description" content={computed.description} />
        {computed.keywords && (
          <meta name="keywords" content={computed.keywords} />
        )}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={computed.themeColor} />
        {computed.url && <link rel="canonical" href={computed.url} />}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={computed.siteName} />
        <meta property="og:title" content={computed.title} />
        <meta property="og:description" content={computed.description} />
        {computed.url && <meta property="og:url" content={computed.url} />}
        {computed.image && (
          <meta property="og:image" content={computed.image} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={computed.title} />
        <meta name="twitter:description" content={computed.description} />
        {computed.image && (
          <meta name="twitter:image" content={computed.image} />
        )}
      </Helmet>

      <Header />
      <main className="page_content service-single-page">
        <PageTitle
          pageTitle={industry.title}
          pagesub="Details"
          pageTop="Industries"
        />

        <section className="service_details_section section_space bg-light">
          <div className="container">
            <div className="details_item_image position-relative">
              <img src={industry.hero?.image} alt={industry.title} />
              <button
                className="video_btn ripple_effect"
                onClick={() => setOpen(true)}
                aria-label="Play video"
              />
            </div>

            {/* Heading */}
            <h2 className="details_item_title">{industry.hero?.heading}</h2>

            {/* Description */}
            {industry.description && (
              <p dangerouslySetInnerHTML={{ __html: industry.description }} />
            )}

            {/* Subheading */}
            {industry.hero?.subheading && (
              <h3 className="details_item_info_title mt-4">
                {industry.hero.subheading}
              </h3>
            )}

            {/* Subdescription as bullet points */}
            {industry.subdescription && industry.subdescription.length > 0 && (
              <ul className="list-disc pl-6 mt-2 space-y-2">
                {industry.subdescription.map((item, index) => (
                  <li
                    key={index}
                    className="text-gray-700"
                    dangerouslySetInnerHTML={{ __html: item }}
                  />
                ))}
              </ul>
            )}

            <WhyChooseUs slug={slug}/>

            {/* Strategies, Features, and Outcomes */}
            <IndustryComplete
              industry={industry} // pulls strategies and features directly
              outcomeIcon={icon} // check icon for list items
              outcomeTitle="Industries Outcome"
            />
          </div>

          {/* Related sections */}
          <IndustriesProjectSection />
          <IndustriesBlogSection />

          {/* FAQs */}
          <Faq
            faqs={industry.faqs || []}
            title={`FAQs for ${industry.title}`}
            defaultOpen={0}
          />
        </section>
      </main>

      <CtaSection />
      <Footer />
      <Scrollbar />

      <ModalVideo
        channel="youtube"
        autoplay
        isOpen={isOpen}
        videoId="eWQy5gOt9Rc"
        onClose={() => setOpen(false)}
      />
    </Fragment>
  );
};

export default IndustriesSinglePage;